import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import EmailPreviewModal from '../components/EmailPreviewModal.jsx';

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}
function relTime(mins) {
  if (mins == null) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

export default function EmailCampaigns() {
  const { user } = useAuth();
  // Which document id we're previewing in the modal, or null when closed.
  const [previewDocId, setPreviewDocId] = useState(null);
  const isSalesperson = user?.role === 'salesperson';
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'opened' | 'unopened'
  const [search, setSearch] = useState('');
  // Doc-type filter — 'all' | 'Email' | 'Quote' | 'Bulloch Request'.
  // Scopes the table to one send-kind. Defaults to all so users see
  // the firm-wide engagement view on open.
  const [docType, setDocType] = useState('all');
  const [syncing, setSyncing] = useState(false);
  // Sender filter — 'me' (default) | 'all' | <user_id>. Salesperson is
  // hard-scoped server-side; the UI hides the dropdown for them too.
  const [sender, setSender] = useState('me');
  // Date range filter — 'all' | 'today' | '7d' | '30d' | 'this_month' | 'last_month'.
  // Defaults to 'all' so the page doesn't hide anything by accident; user
  // can scope by date via the dropdown.
  const [range, setRange] = useState('all');
  // Users list for the Sent-by dropdown (admin/staff only). Fetched
  // once on mount via the existing /api/users/options endpoint that
  // also powers the LeadForm Owner picker.
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  async function handleDelete(emailRow, ev) {
    ev.stopPropagation();
    const recipient = [emailRow.first_name, emailRow.last_name].filter(Boolean).join(' ')
      || emailRow.recipient_email || `row #${emailRow.id}`;
    const status = emailRow.bounced_at   ? 'BOUNCED'
                 : emailRow.opened_at    ? 'OPENED'
                 : emailRow.delivered_at ? 'DELIVERED'
                 :                         'SENT';
    if (!window.confirm(
      `Delete this email-tracking row?\n\n` +
      `Recipient: ${recipient}\n` +
      `Status: ${status}\n\n` +
      `This removes the documents row only — the lead's activity note (if any) stays. Cannot be undone.`
    )) return;
    try {
      await axios.delete(`/api/emails/${emailRow.id}`);
      setEmails((prev) => prev.filter((row) => row.id !== emailRow.id));
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.error || err.message}`);
    }
  }

  async function handleSync() {
    if (!window.confirm(
      'Sync all email statuses from Resend?\n\n' +
      'This calls Resend\'s API once per email document. For the current campaign ' +
      `(~${emails.length} emails) it takes about ${Math.ceil(emails.length * 0.25)} seconds.`
    )) return;
    setSyncing(true);
    try {
      const r = await axios.post('/api/emails/sync-status');
      alert(
        `Sync complete:\n` +
        `• ${r.data.updated} email(s) updated\n` +
        `• ${r.data.no_change} already in sync\n` +
        `• ${r.data.errors.length} error(s)`
      );
      // Reload the list so the new statuses appear
      const fresh = await axios.get('/api/emails');
      setEmails(fresh.data?.emails || []);
    } catch (e) {
      alert(`Sync failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`/api/emails?sender=${encodeURIComponent(sender)}&range=${encodeURIComponent(range)}`)
      .then((r) => { if (!cancelled) setEmails(r.data?.emails || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sender, range]);

  // Users list for the Sent-by dropdown. Salesperson doesn't see the
  // dropdown so skip the round trip for them.
  useEffect(() => {
    if (isSalesperson) return;
    let cancelled = false;
    axios.get('/api/users/options')
      .then((r) => { if (!cancelled) setUsers(r.data?.users || []); })
      .catch(() => { /* dropdown will just show Me / All — non-fatal */ });
    return () => { cancelled = true; };
  }, [isSalesperson]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return emails.filter((e) => {
      // Doc-type scope: when set to anything other than 'all', only
      // rows whose doc_type matches survive. Driven by the Type dropdown.
      if (docType !== 'all' && e.doc_type !== docType) return false;
      if (filter === 'opened' && !e.opened_at) return false;
      if (filter === 'unopened' && e.opened_at) return false;
      if (filter === 'bounced'   && !e.bounced_at)                              return false;
      if (filter === 'delivered' && !(e.delivered_at && !e.opened_at && !e.bounced_at)) return false;
      if (q) {
        // Search hay now includes sender_name so 'mary' or 'sheldon'
        // finds rows by who sent them in addition to who received them.
        const hay = [
          e.first_name, e.last_name, e.company, e.recipient_email,
          e.subject_label, e.sender_name
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [emails, filter, search, docType]);

  const openedCount = emails.filter((e) => e.opened_at).length;
  const totalCount = emails.length;
  const openRate = totalCount > 0 ? Math.round((openedCount / totalCount) * 100) : 0;

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Email Tracking</h2>
          <div className="subtitle">
            {totalCount > 0
              ? `${openedCount} of ${totalCount} opened (${openRate}%)`
              : 'No emails sent yet'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleSync}
            disabled={syncing || totalCount === 0}
            title="Pull latest statuses (delivered / bounced / opened) from Resend"
          >
            {syncing ? 'Syncing…' : '🔄 Sync from Resend'}
          </button>
        </div>
      </div>

      <div className="email-campaigns-toolbar">
        {!isSalesperson && (
          <label className="email-campaigns-sender" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span className="cell-mono" style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-dim)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)' }}>
              Sent by
            </span>
            <select
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              title="Filter by sender — defaults to your own sends"
            >
              <option value="me">Me ({user?.name || 'me'})</option>
              <option value="all">All users</option>
              {users
                .filter((u) => u.id !== user?.id)
                .map((u) => (
                  <option key={u.id} value={String(u.id)}>{u.name}</option>
                ))}
            </select>
          </label>
        )}
        {/* Date range — visible to all roles. Defaults to All Time so the
            page opens with the same data set everyone is used to seeing;
            user can scope by date as needed. */}
        <label className="email-campaigns-range" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span className="cell-mono" style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-dim)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)' }}>
            Date
          </span>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            title="Filter by send date — defaults to all time"
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="this_month">This month</option>
            <option value="last_month">Last month</option>
          </select>
        </label>
        {/* Type filter scopes the table to one send-kind. */}
        <label className="email-campaigns-type" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span className="cell-mono" style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-dim)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)' }}>
            Type
          </span>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            title="Filter by document type"
          >
            <option value="all">All types</option>
            <option value="Email">Email</option>
            <option value="Quote">Quote</option>
            <option value="Bulloch Request">Bulloch Request</option>
          </select>
        </label>
        <div className="email-campaigns-filters">
          {(() => {
            const bouncedCount   = emails.filter((e) => e.bounced_at).length;
            const deliveredCount = emails.filter((e) => e.delivered_at && !e.opened_at && !e.bounced_at).length;
            return (
              <>
                <button type="button"
                  className={`email-campaigns-filter${filter === 'all' ? ' is-active' : ''}`}
                  onClick={() => setFilter('all')}
                >All ({totalCount})</button>
                <button type="button"
                  className={`email-campaigns-filter${filter === 'opened' ? ' is-active' : ''}`}
                  onClick={() => setFilter('opened')}
                >Opened ({openedCount})</button>
                <button type="button"
                  className={`email-campaigns-filter${filter === 'delivered' ? ' is-active' : ''}`}
                  onClick={() => setFilter('delivered')}
                >Delivered ({deliveredCount})</button>
                <button type="button"
                  className={`email-campaigns-filter${filter === 'bounced' ? ' is-active' : ''}`}
                  onClick={() => setFilter('bounced')}
                >Bounced ({bouncedCount})</button>
                <button type="button"
                  className={`email-campaigns-filter${filter === 'unopened' ? ' is-active' : ''}`}
                  onClick={() => setFilter('unopened')}
                >Unopened ({totalCount - openedCount})</button>
              </>
            );
          })()}
        </div>
        <input
          type="search"
          placeholder="Search recipient, company, subject…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="email-campaigns-search"
        />
      </div>

      {loading && <p>Loading…</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Type</th>
                <th>Email</th>
                <th style={{ maxWidth: 280 }}>Subject</th>
                <th>Sent</th>
                <th>Opened</th>
                <th>Opens</th>
                <th style={{ width: 110, minWidth: 110 }}>Status</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="9" className="empty-tab">No emails match.</td></tr>
              )}
              {filtered.map((e) => {
                const opened = !!e.opened_at;
                const destPath = e.record_type === 'lead'
                  ? `/leads/${e.contact_id}`
                  : `/customers/${e.contact_id}`;
                const isQuote   = e.doc_type === 'Quote';
                const isBulloch = e.doc_type === 'Bulloch Request';
                // Recipient label: Bulloch Requests go TO the Bulloch back-office
                // team (the customer is just CC'd), so showing the customer name
                // in the Recipient column was misleading. Override for Bulloch
                // rows: surface the actual recipient (Bulloch) while keeping the
                // customer company below for context — Dave still wants to know
                // which site each Bulloch request was about.
                const customerName = [e.first_name, e.last_name].filter(Boolean).join(' ') || '(no name)';
                const recipientName = isBulloch ? 'Bulloch Back-Office' : customerName;
                const recipientSub  = isBulloch
                  ? (e.company ? `for ${e.company}` : 'configuration request')
                  : e.company;
                const rawSubject = e.subject_label || '';
                // Subject derivation per doc_type:
                //   - Email   → strip the 'email:' prefix, show the template label
                //   - Quote   → take basename of the saved path, drop .docx/.pdf
                //   - Bulloch → same as Quote (saved as uploads/bulloch/.../X.pdf)
                const cleanedSubject = (isQuote || isBulloch)
                  ? rawSubject.split('/').pop().replace(/\.(docx?|pdf)$/i, '')
                  : rawSubject.replace(/^email:/, '');
                return (
                  <tr key={e.id} onClick={() => navigate(destPath)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div>{recipientName}</div>
                      {recipientSub && <div className="cell-mono" style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>{recipientSub}</div>}
                    </td>
                    <td>
                      {isQuote   && <span className="badge badge-purple">QUOTE</span>}
                      {isBulloch && <span className="badge badge-orange">BULLOCH</span>}
                      {!isQuote && !isBulloch && <span className="badge badge-teal">EMAIL</span>}
                    </td>
                    <td className="cell-mono">{isBulloch ? '(bulloch / parkland group)' : (e.recipient_email || '—')}</td>
                    <td
                      style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={cleanedSubject || ''}
                    >
                      {cleanedSubject || '—'}
                    </td>
                    <td className="cell-mono" title={e.sent_at}>{relTime(e.minutes_since_sent)}</td>
                    <td className="cell-mono" title={e.opened_at || ''}>
                      {opened ? relTime(e.minutes_since_opened) : '—'}
                    </td>
                    <td className="cell-mono">{e.open_count || 0}</td>
                    <td>
                      {(() => {
                        if (e.bounced_at)   return <span className="badge badge-red">BOUNCED</span>;
                        if (e.opened_at)    return <span className="badge badge-green">OPENED</span>;
                        if (e.delivered_at) return <span className="badge badge-teal">DELIVERED</span>;
                        return <span className="badge badge-grey">SENT</span>;
                      })()}
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }} onClick={(ev) => ev.stopPropagation()}>
                      {/* View button: opens the sent-email preview modal.
                          Uses the document id (e.id) to fetch the actual
                          subject + body from Resend on demand. */}
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); setPreviewDocId(e.id); }}
                        title="View the sent email"
                        aria-label="View sent email"
                        style={{
                          background: 'none',
                          border: '1px solid var(--color-border)',
                          borderRadius: 4,
                          cursor: 'pointer',
                          color: 'var(--color-text-dim)',
                          fontSize: 11,
                          padding: '2px 8px',
                          marginRight: 6,
                        }}
                      >
                        View
                      </button>
                      {/* Trash button: hard-deletes the documents row. Any */}
                      {/* lead activity note stays. Row dropped from state on success. */}
                      <button
                        type="button"
                        onClick={(ev) => handleDelete(e, ev)}
                        title="Delete this row"
                        aria-label="Delete"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-text-dim)',
                          fontSize: '14px',
                          padding: '4px 8px'
                        }}
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* View sent email modal -- fetches subject + body from Resend
          on demand. Closes when user clicks the backdrop or Close. */}
      {previewDocId && (
        <EmailPreviewModal
          documentId={previewDocId}
          onClose={() => setPreviewDocId(null)}
        />
      )}
    </section>
  );
}
